import React, { useState, useEffect, useRef } from 'react';
import { getSpeakersForConfirmation, resolveSpeaker, completeJob } from '../../services/videoActivationAPI';

const SpeakerConfirmationInline = ({ jobId, videoMediaFileId, onComplete }) => {
  const [data, setData] = useState(null);
  const [currentSpeakerIdx, setCurrentSpeakerIdx] = useState(0);
  const [showSelector, setShowSelector] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resolvedLabels, setResolvedLabels] = useState(new Set());
  const [guestMode, setGuestMode] = useState(false);
  const [guestName, setGuestName] = useState('');
  const audioRef = useRef(null);

  useEffect(() => {
    if (!jobId) return;
    getSpeakersForConfirmation(jobId).then(setData).catch(() => {});
  }, [jobId, videoMediaFileId]);

  if (!data) return null;

  // Flatten speakers — filter to this video if videoMediaFileId is provided
  const allSpeakers = [];
  (data.videos || []).forEach(v => {
    if (videoMediaFileId && v.media_file_id !== videoMediaFileId) return;
    (v.speakers || []).forEach(s => {
      if (!s.confirmed_person_id) {
        allSpeakers.push({ ...s, record_id: v.record_id });
      }
    });
  });

  const unresolvedSpeakers = allSpeakers.filter(s => !resolvedLabels.has(s.speaker_label));
  if (!unresolvedSpeakers.length) return null;

  const persons = data.family_members || [];

  const handleResolve = async (speaker, resolution, personId) => {
    setSaving(true);
    try {
      const result = await resolveSpeaker(
        jobId, speaker.record_id, speaker.speaker_label, resolution, personId
      );
      setResolvedLabels(prev => new Set([...prev, speaker.speaker_label]));
      setShowSelector(false);

      if (result?.is_fully_confirmed) {
        try {
          await completeJob(jobId);
        } catch (e) {
          console.error('Failed to trigger wisdom extraction:', e);
        }
        onComplete && onComplete();
      } else {
        setCurrentSpeakerIdx(i => Math.min(i, unresolvedSpeakers.length - 2));
      }
    } catch (e) {
      console.error('Resolution failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = (speaker, personId) => handleResolve(speaker, 'confirmed', personId);
  const handleOutsideFamily = (speaker) => handleResolve(speaker, 'dismissed');
  const handleGuest = async (speaker) => {
    if (!guestName.trim()) return;
    setSaving(true);
    try {
      const result = await resolveSpeaker(
        jobId, speaker.record_id, speaker.speaker_label, 'guest', null, guestName.trim()
      );
      setResolvedLabels(prev => new Set([...prev, speaker.speaker_label]));
      setShowSelector(false);
      setGuestMode(false);
      setGuestName('');
      if (result?.is_fully_confirmed) {
        try { await completeJob(jobId); } catch {}
        onComplete && onComplete();
      } else {
        setCurrentSpeakerIdx(i => Math.min(i, unresolvedSpeakers.length - 2));
      }
    } catch (e) { console.error('Guest failed:', e); }
    finally { setSaving(false); }
  };

  const playClip = (clipId, url) => {
    if (!url) return;
    if (playingId === clipId) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play().catch(() => {});
        setPlayingId(clipId);
      }
    }
  };

  return (
    <div style={{
      background: '#FFF7F0',
      border: '1px solid #F0C070',
      borderRadius: '10px',
      padding: '12px 14px',
      marginBottom: '10px',
    }}>
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} style={{ display: 'none' }} />

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: '10px',
      }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#7A3000' }}>
          Voices waiting to be identified
        </span>
        <span style={{
          fontSize: '12px', fontWeight: '600',
          background: '#F0C070', color: '#633806',
          borderRadius: '20px', padding: '2px 8px',
        }}>
          {unresolvedSpeakers.length} left
        </span>
      </div>

      {/* Speaker rows */}
      {unresolvedSpeakers.map((speaker, idx) => {
        const sample = speaker.sample_text;
        const audioUrl = speaker.audio_clip_url;
        const isActive = idx === currentSpeakerIdx;
        const clipKey = `${speaker.record_id}_${speaker.speaker_label}`;

        return (
          <div key={clipKey} style={{
            borderBottom: idx < unresolvedSpeakers.length - 1
              ? '0.5px solid #F5E8D8' : 'none',
            paddingBottom: '8px', marginBottom: '8px',
          }}>
            {/* Clip row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                background: '#3a4d1e', borderRadius: '6px',
                padding: '6px 10px', display: 'flex',
                alignItems: 'center', gap: '7px', flex: 1,
                cursor: audioUrl ? 'pointer' : 'default', minHeight: '36px',
              }}
                onClick={() => audioUrl && playClip(clipKey, audioUrl)}
              >
                {/* Play/pause button */}
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {playingId === clipKey
                    ? <span style={{ display: 'flex', gap: '2px' }}>
                        <span style={{ width: '3px', height: '10px', background: 'white', borderRadius: '1px' }} />
                        <span style={{ width: '3px', height: '10px', background: 'white', borderRadius: '1px' }} />
                      </span>
                    : <span style={{
                        width: 0, height: 0,
                        borderStyle: 'solid',
                        borderWidth: '5px 0 5px 9px',
                        borderColor: 'transparent transparent transparent white',
                        marginLeft: '2px',
                      }} />
                  }
                </div>
                <span style={{
                  fontSize: '12px', color: 'rgba(255,255,255,0.85)',
                  fontStyle: 'italic', flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {sample ? `"${sample.slice(0, 60)}..."` : 'No clip available'}
                </span>
              </div>

              {/* Identify button */}
              {isActive && (
                <button
                  onClick={() => setShowSelector(true)}
                  style={{
                    background: 'none', border: 'none',
                    color: '#E65100', fontSize: '13px',
                    fontWeight: '600', cursor: 'pointer',
                    whiteSpace: 'nowrap', padding: '6px 0',
                    minHeight: '36px',
                  }}
                >
                  Who is this? &rarr;
                </button>
              )}
            </div>

            {/* Person selector dropdown */}
            {isActive && showSelector && (
              <div style={{
                marginTop: '8px',
                background: 'white',
                border: '0.5px solid #ddd',
                borderRadius: '8px',
                overflow: 'hidden',
              }}>
                {persons.map(person => (
                  <button
                    key={person.person_id}
                    onClick={() => handleConfirm(speaker, person.person_id)}
                    disabled={saving}
                    style={{
                      width: '100%', background: 'none',
                      border: 'none', borderBottom: '0.5px solid #f5f5f0',
                      padding: '9px 14px', textAlign: 'left',
                      cursor: 'pointer', display: 'flex',
                      alignItems: 'center', gap: '8px',
                      fontSize: '13px', color: '#333',
                      minHeight: '40px',
                    }}
                  >
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%',
                      background: '#556B2F', color: 'white',
                      fontSize: '10px', fontWeight: '600',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {(person.first_name || '?').charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontWeight: '500' }}>{person.first_name} {person.last_name}</div>
                    </div>
                  </button>
                ))}
                {/* Not a family member — guest or dismiss */}
                {!guestMode ? (
                  <button
                    onClick={() => setGuestMode(true)}
                    style={{
                      width: '100%', background: 'none',
                      border: 'none', borderTop: '0.5px solid #f5f5f0',
                      padding: '9px 14px', textAlign: 'left',
                      cursor: 'pointer', display: 'flex',
                      alignItems: 'center', gap: '8px',
                      fontSize: '13px', color: '#888',
                      minHeight: '40px',
                    }}
                  >
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%',
                      background: '#D4D1C4', color: '#fff',
                      fontSize: '9px', fontWeight: '700',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>N/A</div>
                    Not a family member
                  </button>
                ) : (
                  <div style={{ padding: '10px 14px', borderTop: '0.5px solid #f5f5f0' }}>
                    <input
                      value={guestName}
                      onChange={e => setGuestName(e.target.value)}
                      placeholder="Their name (e.g. Coach Williams)"
                      autoFocus
                      style={{
                        width: '100%', padding: '8px 10px', border: '1px solid #DCD9C8',
                        borderRadius: 8, fontSize: 13, outline: 'none',
                        marginBottom: 8, boxSizing: 'border-box',
                      }}
                      onKeyDown={e => { if (e.key === 'Enter' && guestName.trim()) handleGuest(speaker); }}
                    />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleGuest(speaker)} disabled={!guestName.trim() || saving}
                        style={{
                          flex: 1, padding: '7px', borderRadius: 8, border: 'none',
                          background: guestName.trim() ? '#556B2F' : '#E4E2D6',
                          color: guestName.trim() ? '#fff' : '#A7A996',
                          fontSize: 12, fontWeight: 600, cursor: guestName.trim() ? 'pointer' : 'default',
                        }}>Save as Guest</button>
                      <button onClick={() => handleOutsideFamily(speaker)} disabled={saving}
                        style={{
                          padding: '7px 10px', borderRadius: 8, border: '1px solid #DCD9C8',
                          background: '#fff', color: '#888', fontSize: 12, cursor: 'pointer',
                        }}>Skip</button>
                      <button onClick={() => { setGuestMode(false); setGuestName(''); }}
                        style={{
                          padding: '7px 10px', borderRadius: 8, border: 'none',
                          background: 'none', color: '#888', fontSize: 12, cursor: 'pointer',
                        }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SpeakerConfirmationInline;
